import type { EnemyKind } from '../ai/archetypes';
import type { WearableId } from '../economy/wardrobe';

/**
 * Wave L-1 (PLAN-CITY2 §6): `part`/`cosmetic` are the new UNCOMMON->LEGENDARY
 * pickup kinds `economy/drops.ts` rolls (layered on top of this file's own
 * existing coin/material/heart roll, which is UNCHANGED below) — both are
 * real, poolable `Pickup`s (`loot/pickup.ts`) with the same fall/magnet/
 * collect physics every other loot kind already gets, not an instant grant,
 * so L-4's rare-drop juice (a beam-of-light VFX at the drop point) has a
 * real physical moment to attach to.
 */
export type LootKind = 'coin' | 'material' | 'heart' | 'part' | 'cosmetic' | 'collectible';

export interface LootDrop {
  kind: LootKind;
  /** Set only for `kind:'cosmetic'` — which wearable this pickup grants on collection. */
  wearableId?: WearableId;
  /** Set only for `kind:'collectible'` — the id a future placing-wave chose (e.g. `'station_platform'`); main.ts uses it as the key into `save.progress.placedCollectibles`. */
  persistentId?: string;
}

/**
 * Wave I (PLAN-CITY3 §5, I-4): `'collectible'` is a NEW kind for the
 * reusable "persistent world pickup" primitive — never rolled at kill time
 * (it has no `DROP_TABLE` entry, no `rollDrops` involvement at all), only
 * ever placed directly by world-building code via `PickupPool.
 * spawnPersistentCollectible`. It never expires and grants nothing through
 * the normal wallet/health path (`loot/collect.ts` has no branch for it —
 * main.ts's own collection loop reads `persistentId` directly instead, the
 * same "read the extra field the caller actually needs" idiom `wearableId`
 * above already establishes for cosmetics).
 */

interface DropTableEntry {
  coinMin: number;
  coinMax: number;
  materialChance: number;
  heartChance: number;
}

/**
 * Loot config keyed by `EnemyKind`, deliberately kept OUT of
 * `ai/archetypes.ts` — that registry is AI/combat-tuning territory (moveSpeed,
 * attackRange, ...), this one is economy territory, matching the project's
 * existing separation (combat resolution already lives outside `EnemyManager`
 * for the same reason). Tougher/rarer kills pay more and roll a guaranteed
 * material — same shape as Classic's own type-flat, tier-scaled coin formula
 * (Classic gotcha #31).
 */
const DROP_TABLE: Record<EnemyKind, DropTableEntry> = {
  rat: { coinMin: 1, coinMax: 2, materialChance: 0, heartChance: 0.04 },
  pizza_rat: { coinMin: 1, coinMax: 2, materialChance: 0, heartChance: 0.04 },
  pigeon: { coinMin: 1, coinMax: 2, materialChance: 0, heartChance: 0.03 },
  dive_pigeon: { coinMin: 2, coinMax: 3, materialChance: 0.08, heartChance: 0.06 },
  trash_lobber: { coinMin: 2, coinMax: 3, materialChance: 0.15, heartChance: 0.05 },
  rat_king: { coinMin: 8, coinMax: 12, materialChance: 1, heartChance: 0.25 },
  // S11: the golden_pigeon rare-roamer stub — a real, guaranteed-good payout
  // (unlike every other kind above, this is the actual REWARD for tracking
  // one down, not a byproduct of clearing a threat) since the archetype
  // itself is harmless (ai/archetypes.ts's own comment) and rare
  // (respawnDelay ~2.5min) rather than tuned via drop odds.
  golden_pigeon: { coinMin: 12, coinMax: 18, materialChance: 0.6, heartChance: 0 },
  // C08/Wave M: the sewer den elite (PLAN-CITY2 §5 M-3) — sized between the
  // street-tier entries above and rat_king's boss-tier payout, a genuine
  // "worth hunting" elite reward. Placeholder numbers only, same as every
  // other value in this table pre-dates Wave L's real rarity-tier system
  // (PLAN-CITY2 §6 L-1) — exists here purely so `Record<EnemyKind,...>`
  // stays exhaustive; L-1 is the real source of truth once it lands.
  sewer_elite: { coinMin: 4, coinMax: 6, materialChance: 0.4, heartChance: 0.12 },
};

/**
 * Rolled at kill time via `Math.random()` — loot AMOUNTS are per-frame juice
 * (§0.7), not something a restart/replay must reproduce (this block has no
 * seeded level-regen the way Classic's `genLevel` does), so the seeded RNG
 * used for habitat/spawn placement doesn't apply here.
 */
export function rollDrops(kind: EnemyKind): LootDrop[] {
  const entry = DROP_TABLE[kind];
  const drops: LootDrop[] = [];
  const coinCount = entry.coinMin + Math.floor(Math.random() * (entry.coinMax - entry.coinMin + 1));
  for (let i = 0; i < coinCount; i++) drops.push({ kind: 'coin' });
  if (Math.random() < entry.materialChance) drops.push({ kind: 'material' });
  if (Math.random() < entry.heartChance) drops.push({ kind: 'heart' });
  return drops;
}
